import { useCallback } from "react";
import { TimekeeperService } from "services/TimekeeperService";
import { PaycodeCreate } from "repository/timekeeper/@types/PaycodeModel";
import { SnackbarMessage } from "notistack";

interface AlertMessage {
    title: string;
    message: SnackbarMessage;
    severity: "error" | "success" | "warning" | "info";
}

type SetLoading = (loading: boolean) => void;
type Callback = () => void;
type HandlerAlert = (message: AlertMessage) => void;

const timekeeperService = new TimekeeperService();

export const usePaycodesActions = () => {
    const addPaycode = useCallback(async ({
        setLoading,
        body,
        closing,
        reloading,
        messageError,
        messageSuccess,
        handlerAlert,
    }: {
        setLoading: SetLoading;
        body: PaycodeCreate;
        closing: Callback;
        reloading: Callback;
        messageError: AlertMessage;
        messageSuccess: AlertMessage;
        handlerAlert: HandlerAlert;
    }) => {
        setLoading(true);

        try {
            await timekeeperService.paycodes.create(body);
            reloading();
            closing();
            handlerAlert(messageSuccess);
        } catch (error: any) {
            console.error(error?.message || error);
            handlerAlert(messageError);
        } finally {
            setLoading(false);
        }
    }, []);

    const updatePaycode = useCallback(async ({
        setLoading,
        paycodeId,
        body,
        closing,
        reloading,
        messageError,
        messageSuccess,
        handlerAlert,
    }: {
        setLoading: SetLoading;
        paycodeId: number;
        body: PaycodeCreate;
        closing: Callback;
        reloading: Callback;
        messageError: AlertMessage;
        messageSuccess: AlertMessage;
        handlerAlert: HandlerAlert;
    }) => {
        setLoading(true);

        try {
            await timekeeperService.paycodes.update(paycodeId, body);
            reloading();
            closing();
            handlerAlert(messageSuccess);
        } catch (error: any) {
            console.error(error?.message || error);
            handlerAlert(messageError);
        } finally {
            setLoading(false);
        }
    }, []);

    const deletePaycodes = useCallback(async ({
        setLoading,
        paycodeIds,
        closing,
        reloading,
        messageError,
        messageSuccess,
        handlerAlert,
    }: {
        setLoading: SetLoading;
        paycodeIds: number[];
        closing: Callback;
        reloading: Callback;
        messageError: AlertMessage;
        messageSuccess: AlertMessage;
        handlerAlert: HandlerAlert;
    }) => {
        setLoading(true);

        try {
            await Promise.all(paycodeIds.map((paycodeId) => timekeeperService.paycodes.delete(paycodeId)));
            reloading();
            closing();
            handlerAlert(messageSuccess);
        } catch (error: any) {
            console.error(error?.message || error);
            handlerAlert(messageError);
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        addPaycode,
        updatePaycode,
        deletePaycodes,
    };
};
